import { clamp } from 'lodash';
import { parseIntSafe } from './utils';

/**
 * @typedef {import('./number-format-pattern').Modifier} Modifier
 * @typedef {import('./number-format-pattern').ModifierEnum} ModifierEnum
 */

/**
 * Number format pattern class
 */
class NumberFormatPattern {
    /**
     * @type {number}
     */
    digits;

    /**
     * @type {string}
     */
    unit;

    /**
     * @type {Modifier[]}
     */
    modifiers;

    /**
     * @param {{ digits: number, unit: string, modifiers: Modifier[] }} [options={}]
     */
    constructor({ digits = NumberFormatPattern.DIGITS_DEFAULT, unit = '', modifiers = [] } = {}) {
        this.digits = clamp(digits, NumberFormatPattern.DIGITS_MAX);
        this.unit = unit;
        this.modifiers = modifiers;
    }

    /**
     * String representation
     * @return {string}
     */
    toString() {
        const { digits, modifiers, unit } = this;
        return `${digits}|${modifiers.join('')}|${unit}`;
    }
}
/**
 * Factory method
 *
 * @param {string} patternString
 * @return {NumberFormatPattern}
 */
NumberFormatPattern.fromString = (patternString) => {
    const match = patternString.match(/^(\d*)\|(.*)\|(.*)$/);
    if (match == null) {
        throw new Error(`Failed to parse number format pattern: ${patternString}`);
    }
    const [, digitsMatch, modifiersString = '', unit] = match;
    const digits = parseIntSafe(digitsMatch, NumberFormatPattern.DIGITS_MAX);
    /**
     * @type {Modifier[]}
     */
    const modifiers = modifiersString.split('');
    return new NumberFormatPattern({ digits, unit, modifiers });
};

/**
 *
 * @type {ModifierEnum}
 */
NumberFormatPattern.Modifier = Object.freeze({
    PERCENT: '%',
    COMPACT: 'c',
    FIXED: 'f'
});
NumberFormatPattern.DIGITS_MAX = 20;
NumberFormatPattern.DIGITS_DEFAULT = 2;

export default NumberFormatPattern;
